import { useState, useEffect } from 'react'
import { useFillPromptStore } from '../store/useFillPromptStore'
import { useOnlineModeStore } from '../store/useOnlineModeStore'
import { useAuthStore } from '../store/useAuthStore'
import { useTsInfo } from '../hooks/useTsInfo'
import { tsLabel } from '../utils/tsLabel'
import { toFillPeriod } from '../utils/toFillPeriod'
import { ModalOverlay } from './ModalOverlay'

// České skloňování: 1 řada / 2–4 řady / 5+ řad
const radaPlural = (n) => (n === 1 ? 'řada' : n >= 2 && n <= 4 ? 'řady' : 'řad')

// Dialog „Dotáhnout chybějící data?" pro režim 'ask' (viz doc/plans/to-fill-data-fetching.md, krok 7).
// Mountnuto jednou v App. Debounce ~300 ms — víc hooků na obrazovce nabídne joby těsně
// po sobě → jeden dialog se souhrnem.

export function FillPromptDialog() {
  const canFill = useAuthStore((s) => s.canFill)
  const mode = useOnlineModeStore((s) => s.mode)
  const setMode = useOnlineModeStore((s) => s.setMode)
  const pending = useFillPromptStore((s) => s.pending)
  const count = useFillPromptStore((s) => s.count)
  const accept = useFillPromptStore((s) => s.accept)
  const dismiss = useFillPromptStore((s) => s.dismiss)
  const { infoFor } = useTsInfo()

  const n = Object.keys(pending).length
  const [visible, setVisible] = useState(false)
  const [dontAsk, setDontAsk] = useState(false)

  // Debounce zobrazení — počkej, až se nabídky ustálí.
  useEffect(() => {
    if (!canFill || mode !== 'ask' || n === 0) {
      setVisible(false)
      return
    }
    const t = setTimeout(() => setVisible(true), 300)
    return () => clearTimeout(t)
  }, [canFill, mode, n])

  if (!visible) return null

  const total = count()

  // Stanici u řádku ukázat jen když je v nabídce víc stanic (skupinový pohled).
  // U jedné stanice je jasné z kontextu stránky.
  const allRows = Object.values(pending).flatMap((job) =>
    (job.toFill ?? []).map((it) => {
      const info = infoFor(it)
      return {
        name: tsLabel(it, info),
        period: toFillPeriod(it, info),
        ghId: it.params?.gh_id,
      }
    }),
  )
  const ghIds = [...new Set(allRows.map((r) => r.ghId).filter(Boolean))]
  const showStation = ghIds.length > 1
  // dedup podle name+period (+stanice, když se zobrazuje)
  const seen = new Set()
  const rows = allRows.filter((r) => {
    const k = `${r.name}|${r.period}|${showStation ? r.ghId : ''}`
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
  const previewRows = rows.slice(0, 6)
  const moreRows = rows.length - previewRows.length

  const onConfirm = () => {
    if (dontAsk) setMode('auto')
    accept()
    setVisible(false)
  }
  const onCancel = () => {
    dismiss()
    setVisible(false)
  }

  return (
    <ModalOverlay onClose={onCancel} className="z-[9998]">
      <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-gray-800">Dotáhnout chybějící data?</h2>
        <p className="text-sm text-gray-600">
          K dotažení {total === 1 ? 'je' : 'jsou'} <strong>{total}</strong>{' '}
          {radaPlural(total)}.
        </p>
        {previewRows.length > 0 && (
          <ul className="text-xs text-gray-500 space-y-0.5">
            {previewRows.map((r, i) => (
              <li key={i} className="break-words">
                <span className="text-gray-700">{r.name}</span>
                {r.period && <span className="text-gray-400"> · {r.period}</span>}
                {/* Stanice se ukazuje gh_id, ne jménem — ve výpisu dotahovaných řad je
                    podstatný jednoznačný identifikátor (jmen se navíc opakuje víc, např.
                    dvě „Praha"), ne popisek řízený globálním nastavením. */}
                {showStation && r.ghId && (
                  <span className="text-gray-400"> · {r.ghId}</span>
                )}
              </li>
            ))}
            {moreRows > 0 && <li className="text-gray-400">a další {moreRows}…</li>}
          </ul>
        )}

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={dontAsk} onChange={(e) => setDontAsk(e.target.checked)} />
          Příště se neptat (přepnout na automatické dotahování)
        </label>

        <div className="mt-1 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Nedotahovat
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
          >
            Dotáhnout
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}
